import { Card } from "@components/shadcn-ui/card";
import { useIsMobile } from "@customHooks/use-mobile";
import { useDynamicHeight } from "@customHooks/useDynamicHeight";
import { toast } from "@customHooks/useToast";
import { applicationService } from "features/createApplication/service/applicationService";
import { ApplicationStepper } from "features/createApplication/components/ApplicationStepper";
import { useMultiStepForm } from "features/loanApplications/context/MultiStepFormContext";
import { loanFormFields } from "features/loanApplications/formFieldsConfig";
import { MultiStepLoanApplication } from "features/loanApplications/MultiStepForm";
import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { encodeURIComponent } from "@lib/utils";
import { getProductTypeByValue, PRODUCT_TYPES } from "features/applications/config";
import PermissionWrapper from "features/Permission/PermissionWrapper";
import { Action, ApplicationState } from "features/Permission/types";
import Snipper from "@components/ui/Snipper";
import { ArrowLeft, Home } from "lucide-react";
import { addressService } from "features/createApplication/service/addressService";
import { getResidenceVerification } from "../../v2/features/residence-verification/services/rv-service";

const FallBackUI = () => {
  return (
    <p className="flex w-full h-full gap-4 justify-center items-center">
      <Snipper />
      Checking permissions...
    </p>
  );
};

const DontHavePermission = () => {
  return (
    <p className="flex w-full h-full gap-4 justify-center items-center">
      You don't have permission to edit the applications.
    </p>
  );
};

const showToast = (variant: "default" | "destructive", description: string) => {
  toast({
    variant,
    description,
  });
};

const CO_APPLICANT_SECTION = "coApplicantDetails";
const GROUP_DETAILS_SECTION = "groupDetails";
const RESIDENCE_VERIFICATION_SECTION = "residenceVerification";
const REVIEW_SECTION = "review";

const NON_APPLICANT_ADDRESS_TYPES = new Set([
  "CO_APPLICANT_CURRENT",
  "CO_APPLICANT_PERMANENT",
  "REFERENCE_CURRENT",
  "REFERENCE_PERMANENT",
]);

const RV_FORM_SECTION = {
  sectionDescription: "Complete residence verification",
  section: "Residence Verification",
  name: RESIDENCE_VERIFICATION_SECTION,
  fields: [],
  icon: Home,
  isCustomComponent: true,
  customComponentName: "ResidenceVerificationSection",
};

const hasApplicantAddresses = (addresses: Array<{ type?: string }>) =>
  addresses.some((address) => !NON_APPLICANT_ADDRESS_TYPES.has(address.type || ""));

const buildGroupLoanFormSections = (
  sections: any[],
  includeResidenceVerificationSection: boolean
) => {
  const filteredSections = sections.filter(
    (section) =>
      section.name !== CO_APPLICANT_SECTION && section.name !== GROUP_DETAILS_SECTION
  );

  if (!includeResidenceVerificationSection) {
    return filteredSections.filter(
      (section) => section.name !== RESIDENCE_VERIFICATION_SECTION
    );
  }

  const withoutResidenceVerification = filteredSections.filter(
    (section) => section.name !== RESIDENCE_VERIFICATION_SECTION
  );
  const reviewSectionIndex = withoutResidenceVerification.findIndex(
    (section) => section.name === REVIEW_SECTION
  );
  const nextSections = [...withoutResidenceVerification];
  if (reviewSectionIndex === -1) {
    nextSections.push(RV_FORM_SECTION);
    return nextSections;
  }
  // Keep residence verification as the final actionable step before review.
  nextSections.splice(reviewSectionIndex, 0, RV_FORM_SECTION);
  return nextSections;
};

function Header({ action }: { action: string | null }) {
  return (
    <strong className="block mt-0 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm">
      {action === "edit" ? "Edit Application" : "Create Application"}
    </strong>
  );
}

function StepContent({
  currentStep,
  isMobile,
  formFileds,
  goToStep,
  handleAction,
  fieldDisableState,
  applicationSatus,
  productType,
}: {
  applicationFormSteps: any;
  currentStep: any;
  isMobile: any;
  formFileds: any;
  goToStep: any;
  handleAction: any;
  fieldDisableState: any;
  applicationSatus: any;
  productType: any;
}) {
  return (
    <>
      <MultiStepLoanApplication
        goToStep={goToStep}
        currentStep={currentStep}
        formFileds={formFileds}
        handleAction={handleAction}
        fieldDisableState={fieldDisableState}
        isMobile={isMobile}
        applicationStatus={applicationSatus}
        productType={productType}
      />
    </>
  );
}

function ApplicationFormContainer({
  applicationFormSteps,
  currentStep,
  elementRef,
  goToStep,
  formFileds,
  handleAction,
  fieldDisableState,
  applicationSatus,
  productType,
}: any) {
  const isMobile = useIsMobile();

  return (
    <div
      className="flex flex-col py-0 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-100"
      ref={elementRef}
      style={{ height: "calc(100% - 40px)" }}
    >
      {!isMobile && (
        <div className="flex justify-center sticky top-0 m-auto my-8">
          <ApplicationStepper
            stepsDetails={applicationFormSteps}
            onStepChange={goToStep}
            currentStep={currentStep}
          />
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-auto px-2">
        <StepContent
          applicationFormSteps={applicationFormSteps}
          currentStep={currentStep}
          isMobile={isMobile}
          formFileds={formFileds}
          goToStep={goToStep}
          fieldDisableState={fieldDisableState}
          handleAction={handleAction}
          applicationSatus={applicationSatus}
          productType={productType}
        />
      </div>
    </div>
  );
}

function CreateApplicationPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const {
    currentStep,
    goToNextStep,
    goToStep,
    setLeadId,
    setCurrentStep,
    addressIds,
    setAddressIds,
  } = useMultiStepForm();
  const { ref: elementRef, height } = useDynamicHeight();
  const { leadId } = useParams();
  const decodedLeadId = leadId || "";
  const action = searchParams.get("action");
  const isEditMode = action === "edit";
  const id = searchParams.get("id");
  const [applicationFormFields, setApplicationFormFields] =
    useState(loanFormFields);
  const [fetchedApplicationDetails, setFetchedApplicationDetails] = useState<{
    id?: string;
    leadId?: string;
    state?: ApplicationState;
    productType?: string;
    groupDetails?: {
      city?: string;
      id?: string;
      name?: string;
    };
  }>({});
  const [hasResidenceVerification, setHasResidenceVerification] = useState(false);

  // Store leadId in context for use in address form
  useEffect(() => {
    if (decodedLeadId) {
      setLeadId(decodedLeadId);
      console.log("Setting lead ID in context:", decodedLeadId);
    }
  }, [decodedLeadId, setLeadId]);

  // If we're in edit mode and have an ID, also set it in the form
  useEffect(() => {
    if (isEditMode && fetchedApplicationDetails.leadId) {
      setLeadId(fetchedApplicationDetails.leadId);
      console.log("Setting lead ID from fetched details:", fetchedApplicationDetails.leadId);
    }
  }, [isEditMode, fetchedApplicationDetails.leadId, setLeadId]);

  useEffect(() => {
    const fetchFormDetails = async () => {
      try {
        const data = await applicationService.getApplicationById(id!);
        const addressesResponse = await addressService.getAddressesByApplicationId(id!);
        let hasResidenceVerificationFromBackend = false;
        try {
          const residenceVerificationResponse = await getResidenceVerification(id!);
          hasResidenceVerificationFromBackend = !!residenceVerificationResponse?.aggregate;
        } catch (error) {
          hasResidenceVerificationFromBackend = false;
        }
        const applicantAddresses = (addressesResponse.addresses || []).filter(
          (address) => !NON_APPLICANT_ADDRESS_TYPES.has(address.type || "")
        );
        const hasAddress = hasApplicantAddresses(addressesResponse.addresses || []);
        setAddressIds(applicantAddresses.map((address: any) => address.id));
        setFetchedApplicationDetails(data);
        setHasResidenceVerification(hasResidenceVerificationFromBackend);

        let currentFormFields = [...loanFormFields];
        if (data.productType === PRODUCT_TYPES.GROUP_LOAN) {
          currentFormFields = buildGroupLoanFormSections(
            currentFormFields,
            hasAddress || hasResidenceVerificationFromBackend
          );
        }

        const updatedFormData = currentFormFields.map((section: any) => ({
          ...section,
          fields: section.fields.map((field: any) => {
            let defaultValue =
              data[section.name]?.[field.name] || data[field.name];

            // Map API groupDetails format to form field names
            if (section.name === 'groupDetails' && data.groupDetails) {
              if (field.name === 'groupName') {
                defaultValue = data.groupDetails.name;
              } else if (field.name === 'groupCity') {
                defaultValue = data.groupDetails.city;
              } else if (field.name === 'groupId') {
                defaultValue = data.groupDetails.id;
              }
            }

            // Auto-fill PIL Member Name from Personal Details
            if (section.name === 'membershipDetails' && field.name === 'name') {
              const firstName = data.firstName || data.personalDetails?.firstName || "";
              const lastName = data.lastName || data.personalDetails?.lastName || "";
              const autoFilledName = `${firstName} ${lastName}`.trim();
              defaultValue = defaultValue || autoFilledName;
            }

            // Check if the field type is 'productType' and modify the default value accordingly
            if (field.name === "productType") {
              defaultValue =
                getProductTypeByValue(defaultValue) || defaultValue;
            }

            return {
              ...field,
              defaultValue,
            };
          }),
        }));

        setApplicationFormFields(updatedFormData);
        console.log("updatedFormData", updatedFormData);
      } catch (error) {
        console.error("Error fetching application details:", error);
      }
    };

    // Handle initialization from Group Details page navigation state
    const initializeFromLocationState = () => {
      const locationState = location.state as any;
      if (locationState?.groupDetails && locationState?.productType === PRODUCT_TYPES.GROUP_LOAN) {
        console.log("Initializing from location state:", locationState);
        setHasResidenceVerification(false);

        let currentFormFields = [...loanFormFields];
        currentFormFields = buildGroupLoanFormSections(currentFormFields, false);

        const updatedFormData = currentFormFields.map((section: any) => ({
          ...section,
          fields: section.fields.map((field: any) => {
            let defaultValue = field.defaultValue;

            // Prefill product type
            if (field.name === "productType") {
              defaultValue = getProductTypeByValue(PRODUCT_TYPES.GROUP_LOAN) || "Group Loan";
            }

            // Prefill Application ID
            if (section.name === "applicationDetails" && field.name === "id") {
              defaultValue = id;
            }

            return {
              ...field,
              defaultValue,
            };
          }),
        }));

        setApplicationFormFields(updatedFormData);

        setFetchedApplicationDetails({
          id: id!,
          productType: PRODUCT_TYPES.GROUP_LOAN,
          groupDetails: locationState.groupDetails
        });
      }
    };

    // Check if we have location state (coming from Group Details page)
    // This happens when creating a new app, even though action=edit
    const hasLocationState = location.state && (location.state as any).groupDetails;

    console.log("=== CreateApplicationPage useEffect Debug ===");
    console.log("isEditMode:", isEditMode);
    console.log("id:", id);
    console.log("location.state:", location.state);
    console.log("hasLocationState:", hasLocationState);

    if (isEditMode && id && !hasLocationState) {
      // Editing an existing application - fetch from backend
      fetchFormDetails();
    } else if (hasLocationState) {
      // New application from Group Details - use location state
      initializeFromLocationState();
    }
  }, [isEditMode, id, location.state, setAddressIds]);

  useEffect(() => {
    if (fetchedApplicationDetails?.productType !== PRODUCT_TYPES.GROUP_LOAN) {
      return;
    }

    const hasPersistedApplicationId =
      !!fetchedApplicationDetails?.id &&
      fetchedApplicationDetails.id !== "undefined" &&
      fetchedApplicationDetails.id !== "null";
    const shouldShowResidenceVerification =
      hasPersistedApplicationId && (addressIds.length > 0 || hasResidenceVerification);
    setApplicationFormFields((prevFields: any) =>
      buildGroupLoanFormSections(prevFields, shouldShowResidenceVerification)
    );
  }, [
    addressIds,
    fetchedApplicationDetails?.id,
    fetchedApplicationDetails?.productType,
    hasResidenceVerification,
  ]);

  useEffect(() => {
    if (currentStep >= applicationFormFields.length && applicationFormFields.length > 0) {
      setCurrentStep(applicationFormFields.length - 1);
    }
  }, [applicationFormFields.length, currentStep, setCurrentStep]);

  // Generic function to perform the action and handle the response
  const performApplicationAction = async (
    formData: any,
    actionType: string,
    leadId?: string
  ) => {
    try {
      await applicationService.performAction(formData, actionType, leadId);

      // Success toast message
      let successMessage = "";
      if (actionType === "SAVE_APPLICATION") {
        successMessage = "Data Saved Successfully!";
      } else if (actionType === "CREATE_APPLICATION") {
        successMessage = `You have initiated an application successfully..!`;
      } else if (actionType === "SUBMIT_APPLICATION") {
        successMessage = "Application has been submitted Successfully!";
      }

      showToast("default", successMessage);
      goToNextStep();
    } catch (error: any) {
      let errorMessage: string;
      if (actionType === "CREATE_APPLICATION") {
        errorMessage = "Something went wrong... could not initiate application";
      } else if (actionType === "SUBMIT_APPLICATION" && error?.response?.data) {
        // Surface the backend validation message (e.g. RV not completed for GROUP_LOAN)
        errorMessage = typeof error.response.data === "string"
          ? error.response.data
          : "Something went wrong... Please try again";
      } else {
        errorMessage = "Something went wrong... Please try again";
      }
      showToast("destructive", errorMessage);
      console.error(`Error in ${actionType.toLowerCase()}:`, error);
    }
  };

  const saveApplicationForm = async ({ formData }: any) => {
    performApplicationAction(formData, "SAVE_APPLICATION");
  };

  const handleApplicationInitialization = async ({ formData }: any) => {
    performApplicationAction(formData, "CREATE_APPLICATION", decodedLeadId);
  };

  const submitApplicationForm = async ({ formData }: any) => {
    // For GROUP_LOAN, verify residence verification is completed before allowing submission
    if (fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN) {
      try {
        const rvResponse = await getResidenceVerification(fetchedApplicationDetails.id!);
        if (!rvResponse || rvResponse.verificationStatus !== "COMPLETED") {
          showToast(
            "destructive",
            "Residence verification must be completed before submitting a Group Loan application."
          );
          return;
        }
      } catch {
        showToast(
          "destructive",
          "Residence verification must be completed before submitting a Group Loan application."
        );
        return;
      }
    }
    performApplicationAction(formData, "SUBMIT_APPLICATION");
  };

  const handleAction = (actionType: string, formData: Object) => {
    console.log("Action type:", actionType, "Form data:", formData);

    // For Group Loans, map form field names to API format and ensure groupDetails is included
    let enhancedFormData = { ...formData };
    if (fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN) {
      const formDataAny = enhancedFormData as any;
      const locationState = location.state as any;
      const groupDetailsFromState = locationState?.groupDetails;
      const groupDetailsFromFetched = fetchedApplicationDetails?.groupDetails;

      const groupDetails = {
        name: groupDetailsFromState?.name || groupDetailsFromFetched?.name,
        city: groupDetailsFromState?.city || groupDetailsFromFetched?.city,
        id: groupDetailsFromState?.id || groupDetailsFromFetched?.id || decodedLeadId
      };

      enhancedFormData = {
        ...formDataAny,
        groupDetails
      };

      console.log("Enhanced form data with groupDetails:", enhancedFormData);
    }


    // Remove id from payload for CREATE_APPLICATION
    if (actionType === "CREATE_APPLICATION") {
      const formDataAny = enhancedFormData as any;
      if (formDataAny.id) {
        delete formDataAny.id;
      }
      enhancedFormData = { ...formDataAny };
    }

    if (actionType === "SAVE_APPLICATION") {
      saveApplicationForm({ formData: enhancedFormData });
    } else if (actionType === "CREATE_APPLICATION") {
      handleApplicationInitialization({ formData: enhancedFormData });
    } else if (actionType === "SUBMIT_APPLICATION") {
      submitApplicationForm({ formData: enhancedFormData });
    }
  };

  // Filter out Group Loan from product type options when creating a normal application
  useEffect(() => {
    const hasLocationState = location.state && (location.state as any).groupDetails;

    if (!isEditMode && !hasLocationState) {
      setApplicationFormFields(prevFields => {
        return prevFields.map(section => {
          if (section.name === "applicationDetails") {
            return {
              ...section,
              fields: section.fields.map(field => {
                if (field.name === "productType" && field.options) {
                  return {
                    ...field,
                    options: field.options.filter((opt: string) => opt !== "Group Loan")
                  };
                }
                return field;
              })
            };
          }
          return section;
        });
      });
    }
  }, [isEditMode, location.state]);

  const fieldDisableState = {
    id: true,
    productType: true,
  };

  const applicationFormSteps = [...applicationFormFields];

  return (
    <>
      <Link
        to={
          fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN &&
            fetchedApplicationDetails?.leadId === fetchedApplicationDetails?.groupDetails?.id
            ? `/dashboard/groups/${encodeURIComponent(fetchedApplicationDetails?.leadId ?? "")}?city=${encodeURIComponent(fetchedApplicationDetails?.groupDetails?.city ?? "")}`
            : `/dashboard/lead-details/${encodeURIComponent(fetchedApplicationDetails?.leadId ?? "")}`
        }
        className="flex w-fit items-center gap-2 p-2 bg-slate-300 mb-1 rounded-md"
      >
        <ArrowLeft /> {fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN ? "Back to Group details" : "Back to Lead details page"}
      </Link>
      <Card className="relative h-full">
        <PermissionWrapper
          actions={[Action.EDIT_APPLICATION, Action.SUBMIT_APPLICATION]}
          fallback={<FallBackUI />}
        >
          {({ isAllowed }) =>
            isAllowed ? (
              <>
                <Header action={action} />
                <ApplicationFormContainer
                  applicationFormSteps={applicationFormSteps}
                  currentStep={currentStep}
                  id={id}
                  elementRef={elementRef}
                  height={height}
                  formFileds={applicationFormFields}
                  goToStep={goToStep}
                  fieldDisableState={fieldDisableState}
                  handleAction={handleAction}
                  applicationSatus={fetchedApplicationDetails.state}
                  productType={fetchedApplicationDetails.productType}
                />
              </>
            ) : (
              <DontHavePermission />
            )
          }
        </PermissionWrapper>
      </Card>
    </>
  );
}

export default CreateApplicationPage;
